import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Database, 
  Download, 
  Upload, 
  Trash2, 
  RefreshCw, 
  AlertTriangle,
  CheckCircle,
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Backup data - 144 trades from preview (embedded for easy restore)
const BACKUP_USER_ID = "9e6c7beb-5850-4c04-9a52-444d0b0b5eac";

export default function AdminData() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(BACKUP_USER_ID);
  const [exportData, setExportData] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [showConfirmMigrate, setShowConfirmMigrate] = useState(false);
  const [manualTargetUserId, setManualTargetUserId] = useState('');

  const t = {
    fr: {
      title: "Gestion des Données",
      subtitle: "Exporter, importer et gérer vos données de trading",
      selectUser: "Sélectionner un utilisateur",
      currentStats: "Statistiques Actuelles",
      totalTrades: "Total Trades",
      totalPnl: "PNL Total",
      wins: "Gagnants",
      losses: "Perdants",
      active: "Actifs",
      actions: "Actions",
      export: "Exporter les données",
      import: "Importer les données",
      delete: "Supprimer tous les trades",
      refresh: "Rafraîchir",
      exportSuccess: "Données exportées avec succès !",
      importSuccess: "Données importées avec succès !",
      deleteSuccess: "Trades supprimés avec succès !",
      deleteConfirm: "Êtes-vous sûr de vouloir supprimer tous les trades ? Cette action est irréversible !",
      confirm: "Confirmer la suppression",
      cancel: "Annuler",
      noData: "Aucune donnée trouvée",
      loadingUsers: "Chargement des utilisateurs...",
      warning: "Attention",
      warningText: "Ces outils permettent de gérer vos données de trading. Utilisez-les avec précaution.",
      downloadExport: "Télécharger l'export",
      uploadImport: "Charger un fichier d'import",
      importFromFile: "Importer depuis le fichier",
      tradesCount: "trades",
      deletedTrades: "trades supprimés",
      importedTrades: "trades importés",
      migrateToMyAccount: "Migrer vers mon compte",
      migrateConfirm: "Voulez-vous transférer ces trades vers votre compte actuel ?",
      migrateSuccess: "Migration réussie !",
      myAccountId: "Mon compte",
      migratedTrades: "trades migrés"
    },
    en: {
      title: "Data Management",
      subtitle: "Export, import and manage your trading data",
      selectUser: "Select a user",
      currentStats: "Current Statistics",
      totalTrades: "Total Trades",
      totalPnl: "Total PNL",
      wins: "Winners",
      losses: "Losers",
      active: "Active",
      actions: "Actions",
      export: "Export data",
      import: "Import data",
      delete: "Delete all trades",
      refresh: "Refresh",
      exportSuccess: "Data exported successfully!",
      importSuccess: "Data imported successfully!",
      deleteSuccess: "Trades deleted successfully!",
      deleteConfirm: "Are you sure you want to delete all trades? This action cannot be undone!",
      confirm: "Confirm deletion",
      cancel: "Cancel",
      noData: "No data found",
      loadingUsers: "Loading users...",
      warning: "Warning",
      warningText: "These tools allow you to manage your trading data. Use with caution.",
      downloadExport: "Download export",
      uploadImport: "Upload import file",
      importFromFile: "Import from file",
      tradesCount: "trades",
      deletedTrades: "trades deleted",
      importedTrades: "trades imported",
      migrateToMyAccount: "Migrate to my account",
      migrateConfirm: "Do you want to transfer these trades to your current account?",
      migrateSuccess: "Migration successful!",
      myAccountId: "My account",
      migratedTrades: "trades migrated"
    }
  }[language] || {
    title: "Data Management",
    subtitle: "Export, import and manage your trading data",
    selectUser: "Select a user",
    currentStats: "Current Statistics",
    totalTrades: "Total Trades",
    totalPnl: "Total PNL",
    wins: "Winners",
    losses: "Losers",
    active: "Active",
    actions: "Actions",
    export: "Export data",
    import: "Import data",
    delete: "Delete all trades",
    refresh: "Refresh",
    exportSuccess: "Data exported successfully!",
    importSuccess: "Data imported successfully!",
    deleteSuccess: "Trades deleted successfully!",
    deleteConfirm: "Are you sure you want to delete all trades? This action cannot be undone!",
    confirm: "Confirm deletion",
    cancel: "Cancel",
    noData: "No data found",
    loadingUsers: "Loading users...",
    warning: "Warning",
    warningText: "These tools allow you to manage your trading data. Use with caution.",
    downloadExport: "Download export",
    uploadImport: "Upload import file",
    importFromFile: "Import from file",
    tradesCount: "trades",
    deletedTrades: "trades deleted",
    importedTrades: "trades imported",
    migrateToMyAccount: "Migrate to my account",
    migrateConfirm: "Do you want to transfer these trades to your current account?",
    migrateSuccess: "Migration successful!",
    myAccountId: "My account",
    migratedTrades: "trades migrated"
  };

  // Fetch users with trades
  useEffect(() => {
    fetchUsers();
    fetchCurrentUserInfo();
  }, []);

  // Fetch stats when user changes
  useEffect(() => {
    if (selectedUserId) {
      fetchStats();
    }
  }, [selectedUserId]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/data/list-users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCurrentUserInfo = async () => {
    if (!user?.email) return;
    try {
      const response = await axios.get(`${API}/api/admin/data/user-by-email/${user.email}`);
      setCurrentUserInfo(response.data);
    } catch (error) {
      console.error('Error fetching current user info:', error);
    }
  };

  const fetchStats = async () => {
    if (!selectedUserId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/data/stats/${selectedUserId}`);
      setStats(response.data);
      setMessage(null);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(null);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error fetching stats' });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedUserId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/data/export/${selectedUserId}`);
      setExportData(response.data);
      setMessage({ type: 'success', text: `${t.exportSuccess} (${response.data.total_trades} ${t.tradesCount})` });
    } catch (error) {
      console.error('Error exporting:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Export failed' });
    } finally {
      setLoading(false);
    }
  };

  const downloadExport = () => {
    if (!exportData) return;
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tradex_export_${selectedUserId}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setImportFile(data);
          setMessage({ type: 'info', text: `Fichier chargé: ${data.total_trades || data.trades?.length || 0} trades` });
        } catch (error) {
          setMessage({ type: 'error', text: 'Invalid JSON file' });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImport = async () => {
    if (!importFile || !selectedUserId) return;
    
    setLoading(true);
    try {
      const payload = {
        user_id: selectedUserId,
        trades: importFile.trades || [],
        portfolio: importFile.portfolio || null,
        clear_existing: true
      };
      
      const response = await axios.post(`${API}/api/admin/data/import`, payload);
      setMessage({ 
        type: 'success', 
        text: `${t.importSuccess} (${response.data.imported_trades} ${t.importedTrades}, ${response.data.deleted_trades} ${t.deletedTrades})` 
      });
      setImportFile(null);
      fetchStats();
    } catch (error) {
      console.error('Error importing:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Import failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUserId) return;
    
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/api/admin/data/trades/${selectedUserId}`);
      setMessage({ type: 'success', text: `${t.deleteSuccess} (${response.data.deleted_trades} ${t.deletedTrades})` });
      setShowConfirmDelete(false);
      fetchStats();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Delete failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    const targetUserId = manualTargetUserId || currentUserInfo?.user_id;
    
    if (!selectedUserId || !targetUserId) {
      setMessage({ type: 'error', text: 'Veuillez entrer un user_id de destination' });
      return;
    }
    if (selectedUserId === targetUserId) {
      setMessage({ type: 'info', text: 'Les trades appartiennent déjà à ce compte' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/data/migrate-trades?from_user_id=${selectedUserId}&to_user_id=${targetUserId}`
      );
      setMessage({ 
        type: 'success', 
        text: `${t.migrateSuccess} (${response.data.migrated_trades} ${t.migratedTrades})` 
      });
      setShowConfirmMigrate(false);
      // Update selected user to the new one
      setSelectedUserId(targetUserId);
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error migrating:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Migration failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Database className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">{t.title}</h1>
          </div>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-500">{t.warning}</p>
            <p className="text-sm text-yellow-500/80">{t.warningText}</p>
          </div>
        </div>

        {/* FIX BUTTON - One click to inject 144 trades */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
          <h2 className="text-lg font-bold text-green-500 mb-3">🔧 INJECTER MES 144 TRADES</h2>
          <p className="text-sm text-green-400 mb-4">
            Cliquez sur ce bouton pour injecter les 144 trades de backup dans votre compte test@test.com
          </p>
          <button
            onClick={async () => {
              setLoading(true);
              try {
                const response = await axios.post(`${API}/api/admin/data/inject-144-trades`);
                setMessage({ 
                  type: 'success', 
                  text: `✅ ${response.data.message}` 
                });
                fetchUsers();
              } catch (error) {
                setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'injection' });
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-lg font-bold"
            data-testid="inject-trades-btn"
          >
            {loading ? (
              <RefreshCw className="w-6 h-6 animate-spin" />
            ) : (
              <CheckCircle className="w-6 h-6" />
            )}
            INJECTER 144 TRADES
          </button>
        </div>

        {/* User Selection */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">{t.selectUser}</h2>
          </div>
          
          {/* Current user info */}
          {currentUserInfo && (
            <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-sm text-primary">
                <strong>{t.myAccountId}:</strong> {currentUserInfo.email} ({currentUserInfo.user_id.substring(0, 12)}...)
              </p>
            </div>
          )}
          
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary"
            data-testid="user-select"
          >
            <option value="">-- {t.selectUser} --</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.user_id.substring(0, 20)}... ({u.paper_trades} {t.tradesCount})
              </option>
            ))}
          </select>
        </div>

        {/* Stats Display */}
        {stats && (
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">{t.currentStats}</h2>
              </div>
              <button
                onClick={fetchStats}
                disabled={loading}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                data-testid="refresh-stats"
              >
                <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-background rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{stats.total_trades}</p>
                <p className="text-sm text-muted-foreground">{t.totalTrades}</p>
              </div>
              <div className="bg-background rounded-lg p-4 text-center">
                <p className={`text-2xl font-bold ${stats.total_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  ${stats.total_pnl?.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">{t.totalPnl}</p>
              </div>
              <div className="bg-background rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <p className="text-2xl font-bold text-green-500">{stats.by_status?.WIN?.count || 0}</p>
                </div>
                <p className="text-sm text-muted-foreground">{t.wins}</p>
              </div>
              <div className="bg-background rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  <p className="text-2xl font-bold text-red-500">{stats.by_status?.LOSS?.count || 0}</p>
                </div>
                <p className="text-sm text-muted-foreground">{t.losses}</p>
              </div>
              <div className="bg-background rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-500">{stats.by_status?.active?.count || 0}</p>
                <p className="text-sm text-muted-foreground">{t.active}</p>
              </div>
            </div>
          </div>
        )}

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-500' :
            message.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-500' :
            'bg-blue-500/10 border border-blue-500/30 text-blue-500'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : 
             message.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
             <Activity className="w-5 h-5" />}
            <p>{message.text}</p>
          </div>
        )}

        {/* Actions */}
        {selectedUserId && (
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t.actions}</h2>
            
            {/* Export */}
            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={handleExport}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 p-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                data-testid="export-btn"
              >
                <Download className="w-5 h-5" />
                {t.export}
              </button>
              
              {exportData && (
                <button
                  onClick={downloadExport}
                  className="flex-1 flex items-center justify-center gap-2 p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  data-testid="download-export-btn"
                >
                  <Download className="w-5 h-5" />
                  {t.downloadExport} ({exportData.total_trades})
                </button>
              )}
            </div>

            {/* Import */}
            <div className="border-t border-border pt-4">
              <div className="flex flex-col md:flex-row gap-3">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  <div className="flex items-center justify-center gap-2 p-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors cursor-pointer">
                    <Upload className="w-5 h-5" />
                    {t.uploadImport}
                  </div>
                </label>
                
                {importFile && (
                  <button
                    onClick={handleImport}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    data-testid="import-btn"
                  >
                    <Upload className="w-5 h-5" />
                    {t.importFromFile} ({importFile.trades?.length || 0})
                  </button>
                )}
              </div>
            </div>

            {/* Delete */}
            <div className="border-t border-border pt-4">
              {!showConfirmDelete ? (
                <button
                  onClick={() => setShowConfirmDelete(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-red-600/20 text-red-500 border border-red-500/30 rounded-lg hover:bg-red-600/30 transition-colors"
                  data-testid="delete-btn"
                >
                  <Trash2 className="w-5 h-5" />
                  {t.delete}
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-red-500 text-sm">{t.deleteConfirm}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleDelete}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      data-testid="confirm-delete-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                      {t.confirm}
                    </button>
                    <button
                      onClick={() => setShowConfirmDelete(false)}
                      className="flex-1 p-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                      data-testid="cancel-delete-btn"
                    >
                      {t.cancel}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Migrate to My Account */}
            {selectedUserId && (
              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-medium text-foreground mb-3">{t.migrateToMyAccount}</h3>
                
                {/* Show current user info if available */}
                {currentUserInfo && currentUserInfo.user_id !== selectedUserId && (
                  <div className="mb-3 p-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-xs text-purple-400">
                      Votre compte: {currentUserInfo.user_id.substring(0, 20)}...
                    </p>
                  </div>
                )}
                
                {/* Manual target user ID input */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-1 block">
                    User ID de destination (votre compte):
                  </label>
                  <input
                    type="text"
                    value={manualTargetUserId || currentUserInfo?.user_id || ''}
                    onChange={(e) => setManualTargetUserId(e.target.value)}
                    placeholder="Entrez le user_id de destination"
                    className="w-full p-2 bg-background border border-border rounded-lg text-foreground text-sm"
                    data-testid="target-user-id-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Allez sur /api/admin/data/user-by-email/VOTRE_EMAIL pour trouver votre user_id
                  </p>
                </div>
                
                {!showConfirmMigrate ? (
                  <button
                    onClick={() => setShowConfirmMigrate(true)}
                    disabled={!manualTargetUserId && !currentUserInfo?.user_id}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                    data-testid="migrate-btn"
                  >
                    <ArrowRight className="w-5 h-5" />
                    {t.migrateToMyAccount}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-purple-400 text-sm">{t.migrateConfirm}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedUserId.substring(0, 20)}... → {(manualTargetUserId || currentUserInfo?.user_id || '').substring(0, 20)}...
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleMigrate}
                        disabled={loading}
                        className="flex-1 flex items-center justify-center gap-2 p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                        data-testid="confirm-migrate-btn"
                      >
                        <ArrowRight className="w-5 h-5" />
                        {t.confirm}
                      </button>
                      <button
                        onClick={() => setShowConfirmMigrate(false)}
                        className="flex-1 p-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                        data-testid="cancel-migrate-btn"
                      >
                        {t.cancel}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* No user selected */}
        {!selectedUserId && (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Database className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t.selectUser}</p>
          </div>
        )}
      </div>
    </div>
  );
}
